import { bool, elementType, func, node, string } from "prop-types";
import { forwardRef, useContext, useEffect, useMemo, useState } from "react";

import styled from "styled-components/macro";

import { AnimatePresence, motion } from "framer-motion";

import FnrMovingDiv from "UI/FnrMovingDiv";

import { FnrSlidesContext } from "./FnrSlides";
import {
  useActiveKeyAsString,
  useAnimationProps,
  useKeepInDOManimationProps,
} from "./hooks";

const SFnrMovingDiv = styled(FnrMovingDiv)`
  position: relative;
  ${({ widthMotion }) => (!widthMotion ? "width: 100%" : "")};
`;

const AnimationContainer = forwardRef(
  ({ isAnimating, children, clip }, ref) => {
    const { fullWidth, hideHeightMotion, sizeSpring } =
      useContext(FnrSlidesContext);

    return (
      <SFnrMovingDiv
        muted={!isAnimating}
        heightMotion={!hideHeightMotion}
        widthMotion={!fullWidth}
        spring={sizeSpring}
        clip={clip}
        ref={ref}
      >
        {children}
      </SFnrMovingDiv>
    );
  }
);

AnimationContainer.propTypes = {
  isAnimating: bool,
  children: node,
  clip: bool,
};

const KeepInDOMComponent = ({
  Component,
  componentKey,
  onAnimationStart,
  onAnimationComplete,
  ...componentProps
}) => {
  const keepInDOManimationProps = useKeepInDOManimationProps(componentKey);

  return (
    <motion.div
      {...keepInDOManimationProps}
      onAnimationStart={onAnimationStart}
      onAnimationComplete={onAnimationComplete}
    >
      <Component {...componentProps} />
    </motion.div>
  );
};

KeepInDOMComponent.propTypes = {
  componentKey: string,
  Component: elementType,
  onAnimationStart: func,
  onAnimationComplete: func,
};

const MotionContainer = forwardRef(({ clip }, ref) => {
  const { components, componentProps } = useContext(FnrSlidesContext);
  const activeKeyAsString = useActiveKeyAsString();
  const animationProps = useAnimationProps();

  const [activeKey, setActiveKey] = useState(activeKeyAsString);
  const [isAnimating, setIsAnimating] = useState(false);

  // The key should not actually change until 1 render after the exit props update.
  // This allows the component being removed to have its direction updated before being unmounted.
  useEffect(() => {
    setActiveKey(activeKeyAsString);
  }, [activeKeyAsString]);

  const removableComponents = useMemo(
    () => components.filter(({ keepInDOM }) => !keepInDOM),
    [components]
  );

  const permanentComponents = useMemo(
    () => components.filter(({ keepInDOM }) => keepInDOM),
    [components]
  );

  return (
    <AnimationContainer ref={ref} isAnimating={isAnimating} clip={clip}>
      <AnimatePresence initial={false}>
        {removableComponents.map(
          ({ value: Component, key }) =>
            key === activeKey && (
              <motion.div
                {...animationProps}
                key={key}
                onAnimationStart={() => setIsAnimating(true)}
                onAnimationComplete={() => setIsAnimating(false)}
              >
                <Component {...componentProps} />
              </motion.div>
            )
        )}
        {permanentComponents.map(({ value: Component, key }) => (
          <KeepInDOMComponent
            key={key}
            componentKey={key}
            Component={Component}
            onAnimationStart={() => setIsAnimating(true)}
            onAnimationComplete={() => setIsAnimating(false)}
            {...componentProps}
          />
        ))}
      </AnimatePresence>
    </AnimationContainer>
  );
});

MotionContainer.propTypes = { clip: bool };

export default MotionContainer;
